import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { ClipboardText, ArrowsClockwise, FloppyDisk, CheckCircle, MagnifyingGlass, Plus, CircleNotch, ShieldWarning, UploadSimple, DownloadSimple, Tag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { PhysicalInventoryCountAudit } from "@/components/PhysicalInventoryCountAudit";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const keyOf = (productId, warehouseId) => `${productId}|${warehouseId}`;
const fmt = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 }));

const STATUS_BADGE = {
  matched: { label: "Match", className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" },
  shortage: { label: "Shortage", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  excess: { label: "Excess", className: "bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]" },
  blocked: { label: "Blocked", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
};

const REQUEST_BADGE = {
  pending_approval: { label: "Pending approval", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  processing: { label: "Posting to SAP", className: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  completed: { label: "Completed", className: "bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]" },
  partially_posted: { label: "Partially posted", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  stock_changed: { label: "Stock changed", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  rejected: { label: "Rejected", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  failed: { label: "Failed", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  verified_no_adjustment: { label: "Verified, no adjustment", className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" },
};

export const RequestStatusBadge = ({ status }) => {
  const badge = REQUEST_BADGE[status] || { label: status, className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" };
  return <Badge variant="outline" className={`text-[10px] ${badge.className}`} data-testid={`pi-status-${status}`}>{badge.label}</Badge>;
};

const AddItemDialog = ({ open, siteId, category, warehouses, onClose, onAdd }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");

  useEffect(() => { if (!open) { setQuery(""); setResults([]); setWarehouseId(""); } }, [open]);

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/material-search`, { params: { site_id: siteId, category, q: query.trim() } });
      setResults(data.materials || []);
      if (!(data.materials || []).length) toast.info("No material found for that search");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Material search failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px]" data-testid="pi-add-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Add an item with no system stock</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#667085]">
            Found something on the shelf that SAP shows no stock for? Search the material, pick the warehouse it is
            sitting in, and the whole counted quantity becomes an excess (Goods Return) for the administrator to confirm.
          </p>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Warehouse / Logistics Area</Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger data-testid="pi-add-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`pi-add-warehouse-option-${w.warehouse_id}`}>
                    {w.warehouse_id}{w.warehouse_name ? ` · ${w.warehouse_name}` : ""}{w.has_stock === false ? " · no stock on record" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs font-bold text-[#344054]">Material ID or description</Label>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="e.g. 6801-002740P or POLY BAG" data-testid="pi-add-search-input" />
            </div>
            <Button variant="outline" onClick={search} disabled={searching || query.trim().length < 2} data-testid="pi-add-search-btn">
              {searching ? <CircleNotch size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto border border-[#E4E7EC] rounded-lg divide-y divide-[#F2F4F7]">
            {results.map((m) => (
              <div key={m.product_id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`pi-add-result-${m.product_id}`}>
                <div className="min-w-0">
                  <p className="font-mono text-xs font-bold text-[#101828]">{m.product_id}</p>
                  <p className="text-[11px] text-[#667085] truncate">{m.description || "—"} {m.uom ? `· ${m.uom}` : ""}</p>
                  {m.blocked && <p className="text-[11px] text-[#B54708]" data-testid={`pi-add-blocked-${m.product_id}`}>{m.blocked_reason || "No valid SAP price - cannot be counted"}</p>}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={m.blocked || !warehouseId}
                  onClick={() => { onAdd({ ...m, warehouse_id: warehouseId }); onClose(); }}
                  data-testid={`pi-add-select-${m.product_id}`}>
                  <Plus size={12} className="mr-1" /> Add
                </Button>
              </div>
            ))}
            {!results.length && <p className="text-xs text-[#98A2B3] px-3 py-4 text-center">Search a material to add it.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const downloadTemplateFile = async (siteId, category, fmt) => {
  const { data } = await axios.get(`${API}/physical-inventory/template`, {
    params: { site_id: siteId, category, fmt }, responseType: "blob",
  });
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `physical-inventory-${siteId}-${category}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
  a.click();
  URL.revokeObjectURL(url);
};

const BulkUploadDialog = ({ open, siteId, category, tagRequired, onClose, onApply }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { if (!open) { setFile(null); setPreview(null); } }, [open]);

  const downloadTemplate = async (fmt) => {
    setBusy(`template-${fmt}`);
    try {
      await downloadTemplateFile(siteId, category, fmt);
      toast.success("Template downloaded - fill in Physical Qty, Username and Tag No");
    } catch (e) {
      toast.error("Could not build the template");
    } finally { setBusy(""); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy("upload");
    setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("site_id", siteId);
      form.append("category", category);
      const { data } = await axios.post(`${API}/physical-inventory/bulk-upload`, form);
      setPreview(data);
      if (data.clean_count) toast.success(`${data.clean_count} clean row(s) ready to apply`);
      if (data.problem_count) toast.warning(`${data.problem_count} row(s) left out - download them to fix separately`);
      if (!data.clean_count && !data.problem_count) toast.error("Nothing countable in that file");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that file");
    } finally { setBusy(""); }
  };

  /** The rows this file had to leave out (valuation/planning blocks, a tag
      already used, wrong tab...) as their own sheet - the counter fixes those
      and uploads them again while the clean rows go ahead now. */
  const downloadProblems = async () => {
    setBusy("problems");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/problem-rows-file`,
        { site_id: siteId, category, rows: preview.problem_rows }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `physical-inventory-left-out-${siteId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${preview.problem_count} left-out row(s) downloaded`);
    } catch (e) {
      toast.error("Could not build that file");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[680px] w-[calc(100vw-1.5rem)] max-h-[88vh] overflow-y-auto" data-testid="pi-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Bulk upload counts</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <p className="text-xs text-[#667085]">
            Columns: <strong>site, item code, item name, physical qty, warehouse, username{tagRequired === false ? "" : ", tag no"}</strong>.
            One site per file, rows with no quantity are ignored. Rows with a problem of their own are set aside as a
            file you can fix separately - the clean ones can be applied straight away.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("xlsx")} disabled={!!busy || !siteId} data-testid="pi-template-btn">
              {busy === "template-xlsx" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
              Template for {siteId || "site"} (Excel)
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("csv")} disabled={!!busy || !siteId} data-testid="pi-template-csv-btn">
              {busy === "template-csv" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
              CSV
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="file" accept=".csv,.xlsx,.xls,.txt" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-[#D0D5DD] file:bg-white file:text-xs file:font-bold file:text-[#344054]"
              data-testid="pi-bulk-file-input" />
            <Button size="sm" onClick={upload} disabled={!file || busy === "upload"} className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="pi-bulk-upload-btn">
              {busy === "upload" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <UploadSimple size={14} className="mr-1.5" />}
              Check file
            </Button>
          </div>

          {preview && (
            <div className="space-y-2" data-testid="pi-bulk-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs bg-[#F9FAFB] border border-[#E4E7EC] rounded-lg p-3">
                <span className="text-[#667085]">Rows in file</span><span className="font-bold text-[#101828]">{preview.total_rows}</span>
                <span className="text-[#667085]">Ready to apply</span><span className="font-bold text-[#027A48]" data-testid="pi-bulk-counted">{preview.clean_count ?? preview.entries.length}</span>
                <span className="text-[#667085]">Left out</span><span className="font-bold text-[#B42318]" data-testid="pi-bulk-problem-count">{preview.problem_count ?? 0}</span>
                <span className="text-[#667085]">Ignored (no qty)</span><span className="font-bold text-[#101828]">{preview.blank_rows}</span>
                <span className="text-[#667085]">Shortage</span><span className="font-bold text-[#B42318]">{preview.summary?.shortage_items ?? 0}</span>
                <span className="text-[#667085]">Excess</span><span className="font-bold text-[#027A48]">{preview.summary?.excess_items ?? 0}</span>
                <span className="text-[#667085]">Match</span><span className="font-bold text-[#101828]">{preview.summary?.matched_items ?? 0}</span>
              </div>
              {!!preview.error_count && (
                <div className="bg-[#FEF3F2] border border-[#FECDCA] rounded-lg p-3 max-h-56 overflow-y-auto" data-testid="pi-bulk-errors">
                  <p className="text-xs font-bold text-[#B42318] mb-1">{preview.error_count} problem(s) with the file itself - nothing can be applied:</p>
                  {preview.errors.map((err, i) => <p key={i} className="text-[11px] text-[#B42318]">{err}</p>)}
                </div>
              )}
              {!!preview.problem_count && (
                <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-lg p-3" data-testid="pi-bulk-problem-rows">
                  <p className="text-xs font-bold text-[#B54708] mb-1">
                    {preview.problem_count} row(s) cannot be counted and have been left out:
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    {preview.problem_rows.slice(0, 20).map((row) => (
                      <p key={row.row} className="text-[11px] text-[#B54708]" data-testid={`pi-bulk-problem-${row.row}`}>
                        Row {row.row}: {row.product_id}{row.tag_no ? ` · tag ${row.tag_no}` : ""} - {row.reason}
                      </p>
                    ))}
                    {preview.problem_count > 20 && <p className="text-[11px] text-[#B54708]">...and {preview.problem_count - 20} more - all of them are in the file.</p>}
                  </div>
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={downloadProblems}
                    disabled={busy === "problems"} data-testid="pi-bulk-problem-download">
                    {busy === "problems" ? <CircleNotch size={12} className="animate-spin mr-1" /> : <DownloadSimple size={12} className="mr-1" />}
                    Download left-out rows
                  </Button>
                </div>
              )}
              {preview.notes?.length > 0 && (
                <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-lg p-3 max-h-40 overflow-y-auto" data-testid="pi-bulk-notes">
                  <p className="text-xs font-bold text-[#B54708] mb-1">Worth a look (the file can still be applied):</p>
                  {preview.notes.map((note, i) => <p key={i} className="text-[11px] text-[#B54708]">{note}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="w-full sm:w-auto" onClick={onClose} data-testid="pi-bulk-cancel">Cancel</Button>
          <Button className="w-full sm:w-auto bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={!preview?.ok}
            onClick={() => { onApply(preview); onClose(); }} data-testid="pi-bulk-apply-btn">
            Apply {preview?.clean_count ? `${preview.clean_count} clean row(s)` : ""} to count sheet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function PhysicalInventoryPage() {
  const [config, setConfig] = useState({ sites: [], requester_name: "" });
  const [siteId, setSiteId] = useState("");
  const [category, setCategory] = useState("bom");
  const [worksheet, setWorksheet] = useState({ rows: [], stock_as_of: null, blocked_count: 0 });
  const [extraRows, setExtraRows] = useState([]);
  const [entries, setEntries] = useState({});
  const [countedBy, setCountedBy] = useState({});
  const [tagNos, setTagNos] = useState({});
  const [view, setView] = useState("count");
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState("");
  const [query, setQuery] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [warehouses, setWarehouses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [requests, setRequests] = useState([]);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/physical-inventory/config`)
      .then(({ data }) => { setConfig(data); if ((data.sites || []).length === 1) setSiteId(data.sites[0]); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load Physical Inventory"));
  }, []);

  const loadRequests = useCallback(() => {
    axios.get(`${API}/physical-inventory/requests`, { params: { scope: "mine" } })
      .then(({ data }) => setRequests(data.requests || []))
      .catch(() => setRequests([]));
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const loadWorksheet = useCallback(async (refresh = false) => {
    if (!siteId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/worksheet`, { params: { site_id: siteId, category, refresh } });
      setWorksheet(data);
      setPage(0);
      if (refresh) toast.success("Current stock refreshed from SAP");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the count sheet");
      setWorksheet({ rows: [], stock_as_of: null, blocked_count: 0 });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [siteId, category]);

  useEffect(() => {
    let stale = false;
    setEntries({}); setExtraRows([]); setValidation(null); setWarehouseFilter("all"); setQuery(""); setDraftSavedAt(null); setCountedBy({}); setTagNos({});
    if (!siteId) { setWorksheet({ rows: [], stock_as_of: null, blocked_count: 0 }); return undefined; }
    loadWorksheet(false);
    axios.get(`${API}/physical-inventory/warehouses`, { params: { site_id: siteId } })
      .then(({ data }) => { if (!stale) setWarehouses(data.warehouses || []); }).catch(() => setWarehouses([]));
    axios.get(`${API}/physical-inventory/draft`, { params: { site_id: siteId, category } })
      .then(({ data }) => {
        // A fast site/tab switch must never let an older draft land on the new sheet.
        if (stale || !data.entries) return;
        const restored = {};
        (data.entries || []).forEach((e) => { restored[keyOf(e.product_id, e.warehouse_id)] = String(e.physical_qty ?? ""); });
        setEntries(restored);
        setDraftSavedAt(data.saved_at);
      }).catch(() => {});
    return () => { stale = true; };
  }, [siteId, category, loadWorksheet]);

  const allRows = useMemo(() => [...extraRows, ...worksheet.rows], [extraRows, worksheet.rows]);

  // With tag unless an administrator switched this site off (default ON).
  const tagRequired = (config.tag_required || {})[siteId] !== false;

  /** Same item, same godown, two lines - one counted quantity cannot settle two
      stock records, so the whole count is held until it is corrected. The
      backend flags the ones SAP itself duplicates; this also catches a line
      that arrived twice through Add Item or an upload. */
  const duplicates = useMemo(() => {
    const groups = new Map();
    allRows.forEach((r) => {
      const key = keyOf(r.product_id, r.warehouse_id);
      groups.set(key, [...(groups.get(key) || []), r]);
    });
    return [...groups.values()].filter((g) => g.length > 1).map((g) => ({
      product_id: g[0].product_id,
      warehouse_id: g[0].warehouse_id,
      message: (g.find((r) => r.duplicate && r.blocked_reason) || {}).blocked_reason
        || `${g[0].product_id} is on ${g.length} lines for ${g[0].warehouse_id} - an item can only be counted once per godown. Remove the extra line and count the total on one line.`,
    }));
  }, [allRows]);

  const duplicateKeys = useMemo(() => new Set(duplicates.map((d) => keyOf(d.product_id, d.warehouse_id))), [duplicates]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (warehouseFilter !== "all" && r.warehouse_id !== warehouseFilter) return false;
      if (!q) return true;
      return r.product_id.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q);
    });
  }, [allRows, query, warehouseFilter]);

  const pageRows = filteredRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const countedEntries = useMemo(() => Object.entries(entries)
    .filter(([, v]) => String(v ?? "").trim() !== "")
    .map(([k, v]) => {
      const [product_id, warehouse_id] = k.split("|");
      return { product_id, warehouse_id, physical_qty: v, counted_by: countedBy[k] || "", tag_no: tagNos[k] || "" };
    }),
    [entries, countedBy, tagNos]);

  // Every counted line needs its own tag number where the site counts with
  // tags - said here rather than only on submit, so a 60-line count is not
  // rejected at the last step.
  const missingTagCount = tagRequired ? countedEntries.filter((e) => !String(e.tag_no || "").trim()).length : 0;

  const liveSummary = useMemo(() => {
    let shortage = 0, excess = 0, matched = 0, issueQty = 0, returnQty = 0;
    const byKey = new Map(allRows.map((r) => [keyOf(r.product_id, r.warehouse_id), r]));
    countedEntries.forEach((e) => {
      const row = byKey.get(keyOf(e.product_id, e.warehouse_id));
      if (!row) return;
      const physical = Number(e.physical_qty);
      if (Number.isNaN(physical)) return;
      const diff = Number((physical - row.current_qty).toFixed(3));
      if (diff < 0) { shortage += 1; issueQty += -diff; } else if (diff > 0) { excess += 1; returnQty += diff; } else matched += 1;
    });
    return { total: countedEntries.length, shortage, excess, matched, issueQty: Number(issueQty.toFixed(3)), returnQty: Number(returnQty.toFixed(3)) };
  }, [countedEntries, allRows]);

  const setEntry = (row, value) => {
    setValidation(null);
    setEntries((prev) => ({ ...prev, [keyOf(row.product_id, row.warehouse_id)]: value }));
  };

  const rowState = (row) => {
    const raw = entries[keyOf(row.product_id, row.warehouse_id)];
    if (row.blocked) return { status: "blocked", difference: null };
    if (String(raw ?? "").trim() === "") return { status: null, difference: null };
    const physical = Number(raw);
    if (Number.isNaN(physical)) return { status: "invalid", difference: null };
    const difference = Number((physical - row.current_qty).toFixed(3));
    return { status: difference < 0 ? "shortage" : difference > 0 ? "excess" : "matched", difference };
  };

  const saveDraft = async () => {
    setBusy("draft");
    try {
      const { data } = await axios.put(`${API}/physical-inventory/draft`, { site_id: siteId, category, entries: countedEntries });
      setDraftSavedAt(data.saved_at);
      toast.success(`Draft saved - ${data.entry_count} line(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the draft");
    } finally { setBusy(""); }
  };

  const validate = async () => {
    setBusy("validate");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/validate`, { site_id: siteId, category, entries: countedEntries });
      setValidation(data);
      if (data.issues?.length) toast.error(`${data.issues.length} line(s) need attention - see the Issue column`);
      else toast.success(`${data.summary.total_items} line(s) ready: ${data.summary.shortage_items} shortage, ${data.summary.excess_items} excess`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally { setBusy(""); }
  };

  const submit = async () => {
    setBusy("submit");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/submit`, {
        site_id: siteId, category, entries: countedEntries, requester_name: config.requester_name, remarks,
      });
      toast.success(`Submitted as ${data.request_id}`, { description: "Sent to the administrator for confirmation. No SAP transaction has been posted." });
      setConfirmOpen(false);
      setEntries({}); setExtraRows([]); setValidation(null); setRemarks(""); setDraftSavedAt(null); setCountedBy({}); setTagNos({});
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit the physical inventory");
    } finally { setBusy(""); }
  };

  const issueFor = (row) => (validation?.issues || []).find((i) => i.product_id === row.product_id && i.warehouse_id === row.warehouse_id)?.message;

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      await downloadTemplateFile(siteId, category, "xlsx");
      toast.success(`Template for ${siteId} downloaded`, { description: "Fill in Physical Qty and Username, then use Bulk Upload." });
    } catch (e) {
      toast.error("Could not build the template");
    } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="physical-inventory-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ClipboardText size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Physical Inventory</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0"><NavTabs /></div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6 max-w-[1400px] w-full mx-auto">
        <div className="flex items-center gap-2 mb-1.5">
          <ClipboardText size={20} weight="fill" className="text-[#0E7C86]" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Physical Inventory</h1>
        </div>
        <p className="text-sm text-[#667085] max-w-3xl mb-5">
          Count what is physically on the floor, one site and warehouse at a time. Differences go to the administrator
          as an adjustment request - nothing moves in SAP until it is confirmed there.
        </p>

        <Tabs value={view} onValueChange={setView} className="mb-4">
          <TabsList data-testid="pi-view-tabs">
            <TabsTrigger value="count" data-testid="pi-view-count">Count Sheet</TabsTrigger>
            <TabsTrigger value="audit" data-testid="pi-view-audit">Audit</TabsTrigger>
          </TabsList>
        </Tabs>

        {view === "audit" && (
          <PhysicalInventoryCountAudit
            sites={config.sites || []}
            isAdmin={!!config.is_admin}
            tagRequired={config.tag_required || {}}
            onTagSettingChange={(site, required) =>
              setConfig((prev) => ({ ...prev, tag_required: { ...(prev.tag_required || {}), [site]: required } }))}
          />
        )}

        {view === "count" && (<>

        <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-44">
              <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Site</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger className="bg-white" data-testid="pi-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>
                  {(config.sites || []).map((s) => <SelectItem key={s} value={s} data-testid={`pi-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Tabs value={category} onValueChange={setCategory} className="w-full sm:w-auto">
              <TabsList className="flex-wrap h-auto w-full sm:w-auto" data-testid="pi-category-tabs">
                <TabsTrigger value="bom" className="flex-1 sm:flex-none" data-testid="pi-tab-bom">
                  <span className="sm:hidden">BOM</span><span className="hidden sm:inline">BOM Item Inventory</span>
                </TabsTrigger>
                <TabsTrigger value="consumable" className="flex-1 sm:flex-none" data-testid="pi-tab-consumable">
                  <span className="sm:hidden">Consumable</span><span className="hidden sm:inline">Consumable Item Inventory</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Search</Label>
              <Input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} placeholder="Item ID or description"
                disabled={!siteId} data-testid="pi-search-input" />
            </div>
            <div className="w-full sm:w-52">
              <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Warehouse</Label>
              <Select value={warehouseFilter} onValueChange={(v) => { setWarehouseFilter(v); setPage(0); }} disabled={!siteId}>
                <SelectTrigger className="bg-white" data-testid="pi-warehouse-filter"><SelectValue placeholder="All warehouses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All warehouses</SelectItem>
                  {(worksheet.warehouses || []).map((w) => <SelectItem key={w} value={w} data-testid={`pi-warehouse-option-${w}`}>{w}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!!siteId && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-[#F2F4F7]">
              <Button size="sm" variant="outline" onClick={() => loadWorksheet(true)} disabled={refreshing} data-testid="pi-refresh-btn">
                {refreshing ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />}
                Refresh Current Stock
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="pi-add-item-btn">
                <Plus size={14} className="mr-1.5" /> Add Item
              </Button>
              <Button size="sm" variant="outline" onClick={downloadTemplate} disabled={busy === "template"} data-testid="pi-download-template-btn">
                {busy === "template" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
                Download Template
              </Button>
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="pi-bulk-btn">
                <UploadSimple size={14} className="mr-1.5" /> Bulk Upload
              </Button>
              <Button size="sm" variant="outline" onClick={saveDraft} disabled={busy === "draft" || !countedEntries.length} data-testid="pi-save-draft-btn">
                <FloppyDisk size={14} className="mr-1.5" /> Save Draft
              </Button>
              <Button size="sm" variant="outline" onClick={validate} disabled={busy === "validate" || !countedEntries.length} data-testid="pi-validate-btn">
                {busy === "validate" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} className="mr-1.5" />}
                Validate
              </Button>
              <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={!countedEntries.length || !!duplicates.length || !!missingTagCount}
                title={duplicates.length ? "Fix the duplicated item/godown lines first"
                  : missingTagCount ? `${missingTagCount} counted line(s) still have no tag number` : ""}
                className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="pi-submit-btn">
                Submit Physical Inventory
              </Button>
              <div className="ml-auto text-[11px] text-[#667085]" data-testid="pi-stock-as-of">
                {worksheet.stock_as_of ? `Current stock as of ${new Date(worksheet.stock_as_of).toLocaleString()}` : "No stock snapshot yet"}
                {draftSavedAt ? ` · draft saved ${new Date(draftSavedAt).toLocaleTimeString()}` : ""}
              </div>
            </div>
          )}

          {!!missingTagCount && (
            <div className="flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2 mt-3 text-[12px] text-[#B54708]" data-testid="pi-missing-tag-banner">
              <Tag size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>{missingTagCount} counted line(s) have no Tag No yet. {siteId} counts with tag numbers, so write the
                number from the slip on every line before submitting.</span>
            </div>
          )}

          {!!duplicates.length && (
            <div className="flex items-start gap-2 bg-[#FEF3F2] border border-[#FECDCA] rounded-lg px-3 py-2 mt-3 text-[12px] text-[#B42318]" data-testid="pi-duplicate-banner">
              <ShieldWarning size={16} weight="fill" className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-bold">
                  {duplicates.length} item(s) are listed more than once in the same godown - this count is on hold until they are corrected.
                </p>
                {duplicates.slice(0, 5).map((d) => (
                  <p key={`${d.product_id}-${d.warehouse_id}`} data-testid={`pi-duplicate-${d.product_id}-${d.warehouse_id}`}>{d.message}</p>
                ))}
                {duplicates.length > 5 && <p>...and {duplicates.length - 5} more - use the Warehouse filter to find them.</p>}
              </div>
            </div>
          )}

          {!!worksheet.blocked_count && (
            <div className="flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2 mt-3 text-[12px] text-[#B54708]" data-testid="pi-blocked-banner">
              <ShieldWarning size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>{worksheet.blocked_count} item(s) have no valid price in SAP and cannot be counted. Ask the SAP or Master Data team to set their valuation price.</span>
            </div>
          )}
        </div>

        {!siteId && (
          <div className="bg-white border border-[#E4E7EC] rounded-xl p-10 text-center" data-testid="pi-no-site">
            <p className="text-sm text-[#667085]">Select a site to start counting. Stock is never mixed between sites.</p>
          </div>
        )}

        {!!siteId && (
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
            <div className="max-h-[60vh] overflow-auto">
              <Table className="min-w-[1000px]">
                <TableHeader className="sticky top-0 z-10 bg-[#F9FAFB]">
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Item ID</TableHead>
                    <TableHead className="text-xs">Item Name</TableHead>
                    <TableHead className="text-xs w-16">Unit</TableHead>
                    <TableHead className="text-xs text-right w-28">Current Inventory</TableHead>
                    <TableHead className="text-xs">Warehouse / Logistics Area</TableHead>
                    {tagRequired && <TableHead className="text-xs w-40">Tag No</TableHead>}
                    <TableHead className="text-xs w-32">Physical Inventory</TableHead>
                    <TableHead className="text-xs text-right w-24">Difference</TableHead>
                    <TableHead className="text-xs w-44">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={tagRequired ? 9 : 8} className="text-sm text-[#667085] py-8 text-center" data-testid="pi-loading">Loading current stock...</TableCell></TableRow>}
                  {!loading && !filteredRows.length && (
                    <TableRow><TableCell colSpan={tagRequired ? 9 : 8} className="text-sm text-[#667085] py-8 text-center" data-testid="pi-empty">
                      No {category === "bom" ? "BOM" : "consumable"} items with stock at {siteId}. Use Add Item to count something SAP shows no stock for.
                    </TableCell></TableRow>
                  )}
                  {pageRows.map((row) => {
                    const state = rowState(row);
                    const badge = state.status ? STATUS_BADGE[state.status] : null;
                    const issue = issueFor(row);
                    const isDuplicate = duplicateKeys.has(keyOf(row.product_id, row.warehouse_id));
                    const duplicateMessage = isDuplicate
                      ? (duplicates.find((d) => d.product_id === row.product_id && d.warehouse_id === row.warehouse_id) || {}).message
                      : "";
                    return (
                      <TableRow key={keyOf(row.product_id, row.warehouse_id)} data-testid={`pi-row-${row.product_id}-${row.warehouse_id}`}>
                        <TableCell className="font-mono text-xs font-bold text-[#101828]">{row.product_id}</TableCell>
                        <TableCell className="text-xs text-[#344054] max-w-[260px] truncate" title={row.description}>
                          {row.description || "—"}
                          {countedBy[keyOf(row.product_id, row.warehouse_id)] && (
                            <span className="block text-[10px] text-[#98A2B3]" data-testid={`pi-counted-by-${row.product_id}-${row.warehouse_id}`}>
                              counted by {countedBy[keyOf(row.product_id, row.warehouse_id)]}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#667085]">{row.uom}</TableCell>
                        <TableCell className="text-xs text-[#344054] text-right tabular-nums" data-testid={`pi-current-${row.product_id}-${row.warehouse_id}`}>{fmt(row.current_qty)}</TableCell>
                        <TableCell className="text-xs text-[#344054]">
                          <span className="font-mono">{row.warehouse_id}</span>
                          <span className="text-[#98A2B3]"> · {row.warehouse_name}</span>
                        </TableCell>
                        {tagRequired && (
                          <TableCell>
                            <Input
                              value={tagNos[keyOf(row.product_id, row.warehouse_id)] ?? ""}
                              onChange={(e) => { setValidation(null); setTagNos((prev) => ({ ...prev, [keyOf(row.product_id, row.warehouse_id)]: e.target.value.toUpperCase().replace(/\s/g, "") })); }}
                              disabled={row.blocked || isDuplicate}
                              placeholder="Tag no"
                              maxLength={32}
                              className={`h-8 w-36 text-xs font-mono ${row.blocked || isDuplicate ? "bg-[#F2F4F7] cursor-not-allowed" : ""}`}
                              data-testid={`pi-tag-input-${row.product_id}-${row.warehouse_id}`}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Input
                            type="number" min="0" step="any" inputMode="decimal"
                            value={entries[keyOf(row.product_id, row.warehouse_id)] ?? ""}
                            onChange={(e) => setEntry(row, e.target.value)}
                            disabled={row.blocked || isDuplicate}
                            title={isDuplicate ? duplicateMessage : row.blocked ? row.blocked_reason : ""}
                            className={`h-8 text-xs ${row.blocked || isDuplicate ? "bg-[#F2F4F7] cursor-not-allowed" : ""}`}
                            data-testid={`pi-input-${row.product_id}-${row.warehouse_id}`}
                          />
                        </TableCell>
                        <TableCell className={`text-xs text-right tabular-nums font-bold ${state.difference < 0 ? "text-[#B42318]" : state.difference > 0 ? "text-[#027A48]" : "text-[#667085]"}`}
                          data-testid={`pi-diff-${row.product_id}-${row.warehouse_id}`}>
                          {state.difference === null ? "" : state.difference > 0 ? `+${fmt(state.difference)}` : fmt(state.difference)}
                        </TableCell>
                        <TableCell>
                          {(badge || isDuplicate) && <Badge variant="outline" className={`text-[10px] ${isDuplicate ? "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" : badge.className}`} title={isDuplicate ? duplicateMessage : row.blocked ? row.blocked_reason : ""} data-testid={`pi-badge-${row.product_id}-${row.warehouse_id}`}>{isDuplicate ? "Duplicate" : badge.label}</Badge>}
                          {isDuplicate && <p className="text-[10px] text-[#B42318] mt-1" data-testid={`pi-duplicate-row-${row.product_id}-${row.warehouse_id}`}>{duplicateMessage}</p>}
                          {issue && !isDuplicate && <p className="text-[10px] text-[#B42318] mt-1" data-testid={`pi-issue-${row.product_id}-${row.warehouse_id}`}>{issue}</p>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {filteredRows.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-[#E4E7EC] text-xs text-[#667085]">
                <span data-testid="pi-page-info">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="pi-prev-page">Previous</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} data-testid="pi-next-page">Next</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <h2 className="font-heading text-base font-bold text-[#101828] mb-2">My Requests</h2>
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow className="bg-[#F9FAFB]">
                  <TableHead className="text-xs">Request ID</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Site</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs text-right">Items</TableHead>
                  <TableHead className="text-xs text-right">Shortage</TableHead>
                  <TableHead className="text-xs text-right">Excess</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!requests.length && <TableRow><TableCell colSpan={8} className="text-sm text-[#667085] py-6 text-center" data-testid="pi-no-requests">No physical inventory submitted yet.</TableCell></TableRow>}
                {requests.map((r) => (
                  <TableRow key={r.request_id} data-testid={`pi-request-row-${r.request_id}`}>
                    <TableCell className="font-mono text-xs font-bold text-[#101828]">{r.request_id}</TableCell>
                    <TableCell className="text-xs text-[#667085]">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-[#344054]">{r.site_id}</TableCell>
                    <TableCell className="text-xs text-[#344054]">{r.category === "bom" ? "BOM" : "Consumable"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.summary?.total_items ?? 0}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-[#B42318]">{r.summary?.shortage_items ?? 0}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-[#027A48]">{r.summary?.excess_items ?? 0}</TableCell>
                    <TableCell><RequestStatusBadge status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        </>)}
      </div>

      <AddItemDialog
        open={addOpen} siteId={siteId} category={category} warehouses={warehouses}
        onClose={() => setAddOpen(false)}
        onAdd={(m) => {
          const exists = allRows.some((r) => r.product_id === m.product_id && r.warehouse_id === m.warehouse_id);
          if (exists) { toast.info(`${m.product_id} is already on the sheet for ${m.warehouse_id}`); return; }
          setExtraRows((prev) => [{
            product_id: m.product_id, description: m.description, uom: m.uom, warehouse_id: m.warehouse_id,
            warehouse_name: (warehouses.find((w) => w.warehouse_id === m.warehouse_id) || {}).warehouse_name || m.warehouse_id,
            current_qty: 0, unit_cost: m.unit_cost, blocked: m.blocked, blocked_reason: m.blocked_reason, added: true,
          }, ...prev]);
          toast.success(`${m.product_id} added for ${m.warehouse_id}`);
        }}
      />

      <BulkUploadDialog
        open={bulkOpen} siteId={siteId} category={category} tagRequired={tagRequired}
        onClose={() => setBulkOpen(false)}
        onApply={(preview) => {
          const nextEntries = {}, nextCounted = {}, nextTags = {}, added = [];
          (preview.lines || []).forEach((l) => {
            const key = keyOf(l.product_id, l.warehouse_id);
            nextEntries[key] = String(l.physical_qty);
            if (l.counted_by) nextCounted[key] = l.counted_by;
            if (l.tag_no) nextTags[key] = l.tag_no;
            const onSheet = worksheet.rows.some((r) => r.product_id === l.product_id && r.warehouse_id === l.warehouse_id);
            if (!onSheet) {
              added.push({
                product_id: l.product_id, description: l.description, uom: l.uom, warehouse_id: l.warehouse_id,
                warehouse_name: l.warehouse_name || l.warehouse_id, current_qty: l.current_qty,
                unit_cost: l.unit_cost, blocked: false, blocked_reason: "", added: true,
              });
            }
          });
          setExtraRows((prev) => [
            ...added.filter((a) => !prev.some((p) => p.product_id === a.product_id && p.warehouse_id === a.warehouse_id)),
            ...prev,
          ]);
          setEntries((prev) => ({ ...prev, ...nextEntries }));
          setCountedBy((prev) => ({ ...prev, ...nextCounted }));
          setTagNos((prev) => ({ ...prev, ...nextTags }));
          setValidation(null);
          toast.success(`${(preview.lines || []).length} line(s) applied - review the sheet and submit`);
        }}
      />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="pi-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Submit physical inventory?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <span className="text-[#667085]">Site</span><span className="font-bold text-[#101828]" data-testid="pi-confirm-site">{siteId}</span>
              <span className="text-[#667085]">Inventory type</span><span className="font-bold text-[#101828]">{category === "bom" ? "BOM" : "Consumable"}</span>
              <span className="text-[#667085]">Requester</span><span className="font-bold text-[#101828]">{config.requester_name}</span>
              <span className="text-[#667085]">Total items counted</span><span className="font-bold text-[#101828]" data-testid="pi-confirm-total">{liveSummary.total}</span>
              <span className="text-[#667085]">Shortage items</span><span className="font-bold text-[#B42318]" data-testid="pi-confirm-shortage">{liveSummary.shortage}</span>
              <span className="text-[#667085]">Excess items</span><span className="font-bold text-[#027A48]" data-testid="pi-confirm-excess">{liveSummary.excess}</span>
              <span className="text-[#667085]">Matched items</span><span className="font-bold text-[#101828]">{liveSummary.matched}</span>
              <span className="text-[#667085]">Total Goods Issue qty</span><span className="font-bold text-[#101828]">{fmt(liveSummary.issueQty)}</span>
              <span className="text-[#667085]">Total Goods Return qty</span><span className="font-bold text-[#101828]">{fmt(liveSummary.returnQty)}</span>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Remarks</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                placeholder="Anything the administrator should know" data-testid="pi-remarks-input" />
            </div>
            <p className="text-xs text-[#667085] bg-[#F9FAFB] border border-[#E4E7EC] rounded-lg p-2.5">
              Any stock differences will be sent to the administrator for confirmation.
              <strong> No SAP stock transaction will be posted until administrator approval.</strong>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="pi-confirm-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy === "submit"} className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="pi-confirm-submit">
              {busy === "submit" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null} Confirm &amp; Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
